import React, { useState } from 'react';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Button, Grid, IconButton, InputAdornment, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-mui';
import { useNavigate } from 'react-router-dom';
import { breadLogicUtils_getBaseRoutingLogicObjects } from 'src/e2e-redesign/business-logic/bread-logic/bread-logic-utils';
import { e2eConfigLogic_parseTemplate } from 'src/e2e-redesign/business-logic/e2e-configuration-object-logic';
import {
  E2eLogicObjectsWithParams,
  e2eLogicUtils_getBaseRoutingLogicObjects,
} from 'src/e2e-redesign/business-logic/e2e-logic-utils';
import ThemedHtml from 'src/e2e-redesign/components/ThemedHtml/ThemedHtml';
import { CreateAccountFormValuesI } from 'src/e2e-redesign/views-bread/BreadCreateAccountNewCustomer/bread-logic/bread-create-account-logic';
import { DisclosureTypeE } from 'src/interfaces/disclosures.interfaces';

import CustomCheckbox from './BreadCustomCheckbox';
import FpDisclosureDialog from './BreadFpDisclosureDialog';
import BreadMultiCheckbox from './BreadMultiCheckbox';
import PasswordRequirements from './BreadPasswordRequirements';
import { validationSchema } from './formSchemaBread';

interface FormField {
  name: string;
  label: string;
  type: string;
}

const listItems: string[] = [
  'By providing my mobile number, I agree that FormPiper and Premier Service Group may contact me at this number regarding my account for any and all purposes.',
  'By checking each box, I verify that I have read and understand the terms, policies and notices.',
  'By clicking CONTINUE, I authorize that all information I have provided to the FormPiper application, including any and all personal and financial data may be collected and stored by FormPiper in accordance with the purposes and provisions in the FormPiper Privacy Policy. I further acknowledge and authorize FormPiper to share my data provided in my application(s) to providers for the purpose of evaluating my information for prospective grants of financing/leasing. Form Piper may share my contact data with Affiliates that may offer relevant services or data to you.',
  'I attest that the information provided above is true and accurate.',
];

const passwordRequirements: string[] = [
  'At least 8 characters',
];

export interface CreateAccountFormProps {
  initialValues: CreateAccountFormValuesI;
  onSubmit: (values: CreateAccountFormValuesI) => Promise<boolean>;
  getInputProps: (field: string, endAdornment: any) => any;
  handleIsDisabled: (values: CreateAccountFormValuesI) => boolean;
}
const CreateAccountForm: React.FC<CreateAccountFormProps> = (props: CreateAccountFormProps) => {
  const routingPackage: E2eLogicObjectsWithParams = breadLogicUtils_getBaseRoutingLogicObjects();
  const e2eStore = routingPackage.e2eStore;
  const disclosures = routingPackage.e2eStore.disclosures;
  const bullets = disclosures.find((d) => d.disclosure_type === DisclosureTypeE.fp_bullets);
  const { initialValues, onSubmit, getInputProps, handleIsDisabled } = props;
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<string | null>(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const processorName = e2eStore.app_configurations?.processor_details?.store_name;
  initialValues.fp_bullets.diclosure_id = bullets?._id ?? initialValues.fp_bullets.diclosure_id;
  const [password, setPassword] = useState<string>('');
  const [isPasswordFocused, setIsPasswordFocused] = useState<boolean>(false);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleFocus = () => {
    setIsPasswordFocused(true);
  };

  const handleBlur = () => {
    if (password.length === 0) {
      setIsPasswordFocused(false);
    }
  };

  const allRequirementsSatisfied = () => {
    return (
      password.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /\d/.test(password) &&
      /[@$!%*?&#]/.test(password)
    );
  };

  const endAdornment = () => (
    <InputAdornment position="end">
      <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword(!showPassword)} edge="end">
        {showPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  const formFields: FormField[] = [
    { name: 'first_name', label: "Borrower's First Name", type: 'text' },
    { name: 'last_name', label: "Borrower's Last Name", type: 'text' },
    { name: 'email', label: 'Email Address', type: 'email' },
    { name: 'mobile_phone', label: 'Mobile Phone Number', type: 'text' },
    { name: 'password', label: 'Create Password', type: showPassword ? 'text' : 'password' },
    { name: 'confirm_password', label: 'Confirm Password', type: showPassword ? 'text' : 'password' },
  ];

  const handleOpenDialog = (dialogType: string) => {
    setOpenDialog(dialogType);
  };

  const handleCloseDialog = () => {
    setOpenDialog(null);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await onSubmit(values);
        } catch (error) {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, values, handleChange }) => (
        <Form>
          <Grid container spacing={2}>
            {formFields.map((field) => (
              <Grid
                item
                xs={12}
                sm={
                  field.name === 'first_name' ||
                  field.name === 'last_name' ||
                  field.name === 'email' ||
                  field.name === 'mobile_phone' ||
                  field.name === 'password' ||
                  field.name === 'confirm_password'
                    ? 6
                    : 12
                }
                key={field.name}
              >
                <Field
                  component={TextField}
                  name={field.name}
                  label={field.label}
                  type={field.type}
                  className={`fs-mask`}
                  fullWidth
                  sx={{ '& .MuiInputBase-root': { borderRadius: '15px' } }}
                  InputProps={getInputProps(field.name, endAdornment)}
                  onChange={
                    field.name === 'password'
                      ? (e: React.ChangeEvent<HTMLInputElement>) => {
                          handleChange(e);
                          handlePasswordChange(e);
                        }
                      : handleChange
                  }
                  onFocus={field.name === 'password' ? handleFocus : undefined}
                  onBlur={field.name === 'password' ? handleBlur : undefined}
                />
              </Grid>
            ))}
            {isPasswordFocused && password.length > 0 && !allRequirementsSatisfied() && (
              <Grid item xs={12}>
                <PasswordRequirements requirements={passwordRequirements} password={password} />
              </Grid>
            )}
            <Grid item xs={12} sx={{ mb: 1 }}>
              <BreadMultiCheckbox setOpenDialog={setOpenDialog} />
            </Grid>
            <Grid item xs={12}>
              <Typography paragraph variant="body1">
                {bullets && (
                  // <ThemedHtml
                  //   htmlContent={e2eConfigLogic_parseTemplate(bullets.document, {
                  //     fpAddAdditionalEntitied: processorName
                  //       ? `, ${e2eStore.app_configurations?.store_details?.store_name} and ${e2eStore.app_configurations?.processor_details?.store_name}`
                  //       : ` and ${e2eStore.app_configurations?.store_details?.store_name}`,
                  //   })}
                  // />
                  <ThemedHtml htmlContent={bullets.document} />
                )}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: { md: 'flex-start' },
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                color={'warning'}
                size="large"
                type="submit"
                disabled={handleIsDisabled(values) || isSubmitting}
              >
                Continue
              </Button>
              <Button
                type="button"
                onClick={() => navigate(-1)}
                sx={{
                  color: theme.palette.text.primary,
                  textDecoration: 'underline',
                  marginLeft: { xs: 0, md: 6 },
                }}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>

          <FpDisclosureDialog open={openDialog !== null} onClose={handleCloseDialog} dialogType={openDialog} />
        </Form>
      )}
    </Formik>
  );
};

export default CreateAccountForm;
